import React, { useEffect, useState } from "react";
import "./styles.css";
import { RootState } from "Realty/store";
import {
  selectInstallmentPlanTypesError,
  selectInstallmentPlanTypesList,
  selectInstallmentPlanTypesPending,
  selectPricesByFirstPaymentData,
  selectPricesByFirstPaymentError,
  selectPricesByFirstPaymentPending,
  selectPricesByPeriodData,
  selectPricesByPeriodError,
  selectPricesByPeriodPending,
  selectCommerceInstallmentPlanTypesError,
  selectCommerceInstallmentPlanTypesList,
  selectCommerceInstallmentPlanTypesPending,
  selectCommercePricesByFirstPaymentData,
  selectCommercePricesByFirstPaymentError,
  selectCommercePricesByFirstPaymentPending,
  selectCommercePricesByPeriodData,
  selectCommercePricesByPeriodError,
  selectCommercePricesByPeriodPending,
} from "../../../store/selectors";
import { HousingsActionCreator } from "../../../store/housingsActions";
import { connect } from "react-redux";
import Accordion from "../Accordion";
import { InstallmentPlan } from "./InstallmentPlan";
import { InstallmentPlanSkeleton } from "./InstallmentPlan/InstallmentPlanSkeleton";
import { Error } from "../Error";
import { PriceList } from "./PriceList";
import { INSTALLMENT_PLAN_TYPES } from "./constants";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useBranding } from "../../../integrations/branding";

type PricingType = "residential" | "commerce";

type OwnProps = {
  type?: PricingType; // по умолчанию residential
  housing: any;
  shareData?: any;
  setShareData?: (v: any) => void;
  showPriceList?: boolean;
  showCalculator?: boolean;
  calculatorEyebrow?: string;
  calculatorTitle?: string;
  mobileOpenLabel?: string;
  mobileCloseLabel?: string;
};

type StateProps = {
  installmentPlans: any;
  pending: boolean;
  error: boolean;
  pricesByPeriod: any;
  pendingByPeriod: boolean;
  errorByPeriod: boolean;
  pricesByFirstPayment: any;
  pendingByFirstPayment: boolean;
  errorByFirstPayment: boolean;
};

type DispatchProps = {
  fetchInstallmentPlanTypes: (housingName: string) => void;
  fetchPricesByPeriod: (housingName: string) => void;
  fetchPricesByFirstPayment: (housingName: string) => void;
};

export const _Pricing: React.FC<OwnProps & StateProps & DispatchProps> = ({
                                                                            type = "residential",
                                                                            housing,
                                                                            installmentPlans,
                                                                            pending,
                                                                            error,
                                                                            pricesByPeriod,
                                                                            pendingByPeriod,
                                                                            errorByPeriod,
                                                                            pricesByFirstPayment,
                                                                            pendingByFirstPayment,
                                                                            errorByFirstPayment,
                                                                            fetchInstallmentPlanTypes,
                                                                            fetchPricesByPeriod,
                                                                            fetchPricesByFirstPayment,
                                                                            shareData,
                                                                            setShareData,
                                                                            showPriceList = true,
                                                                            showCalculator = true,
                                                                            calculatorEyebrow = "Калькулятор",
                                                                            calculatorTitle = "Рассчитать рассрочку",
                                                                            mobileOpenLabel = "Открыть калькулятор",
                                                                            mobileCloseLabel = "Скрыть калькулятор",
                                                                          }) => {
  const { isShowcase } = useBranding();
  const [option, setOption] = useState<any>();
  const isMobile = useIsMobile();
  const pendingPrices = pending || pendingByPeriod || pendingByFirstPayment;
  const errorPrices = error || errorByPeriod || errorByFirstPayment;

  const hasMultiTypes =
      installmentPlans && Object.keys(installmentPlans)?.length > 1;

  useEffect(() => {
    if (!!installmentPlans && hasMultiTypes) {
      setOption(INSTALLMENT_PLAN_TYPES.byFirstPayment.id);
    }
    if (!!installmentPlans && !hasMultiTypes) {
      setOption(INSTALLMENT_PLAN_TYPES[Object.keys(installmentPlans)]?.id);
    }
  }, [installmentPlans]);

  useEffect(() => {
    if (installmentPlans && !pendingByFirstPayment) {
      fetchPricesByFirstPayment(housing.housingName);
    }
    if (installmentPlans && !pendingByPeriod) {
      fetchPricesByPeriod(housing.housingName);
    }
  }, [installmentPlans]);

  useEffect(() => {
    fetchInstallmentPlanTypes(housing.housingName);
  }, []);

  if (pendingPrices) {
    return <InstallmentPlanSkeleton />;
  }

  if (errorPrices) {
    return (
        <Error
            title="Данные о ценах не загрузились"
            retryCallback={() => fetchInstallmentPlanTypes(housing.housingName)}
        />
    );
  }

  if (
      pricesByPeriod?.floorRanges?.length === 0 &&
      pricesByFirstPayment?.floorRanges?.length === 0
  ) {
    return (
        <Error
            title="Данные о ценах отсутствуют"
            description="Возможно, скоро они появятся."
        />
    );
  }

  return (
      <div className={`space-y-5 ${isShowcase ? "pricing-showcase" : ""}`.trim()}>
        {installmentPlans && (
            <>
              {showPriceList && (
                <section className="space-y-4">
                  <PriceList
                      pricesByFirstPayment={pricesByFirstPayment}
                      pricesByPeriod={pricesByPeriod}
                  />
                </section>
              )}

              {showCalculator && (
                <section
                  className="border-t pt-5 sm:rounded-[24px] sm:border sm:p-5"
                  style={{
                    borderColor: "var(--brand-border, #e7ebf0)",
                    background: "var(--brand-surface-elevated, #ffffff)",
                  }}
                >
                  <div className="mb-4">
                    <div
                      className="text-[12px] font-semibold uppercase tracking-[0.12em]"
                      style={{ color: "var(--brand-text-secondary, #7a8699)" }}
                    >
                      {calculatorEyebrow}
                    </div>
                    <h2
                      className="mt-2 text-[28px] font-semibold leading-[1] sm:text-[32px]"
                      style={{ color: "var(--brand-text-primary, #101828)" }}
                    >
                      {calculatorTitle}
                    </h2>
                  </div>

                  {isMobile ? (
                    <Accordion
                        details={
                          <InstallmentPlan
                              option={option}
                              setOption={setOption}
                              installmentPlans={installmentPlans}
                              housing={housing}
                              shareData={shareData}
                              setShareData={setShareData}
                              hasMultiTypes={hasMultiTypes}
                              type={type}
                          />
                        }
                        moreButton={mobileOpenLabel}
                        lessButton={mobileCloseLabel}
                        buttonFullWidth
                        moreButtonClassName="!mt-0 !rounded-[16px] !px-5 !py-3 !text-[14px] !font-semibold"
                        detailsClassName="pt-3 sm:pt-4"
                    />
                  ) : (
                    <InstallmentPlan
                        option={option}
                        setOption={setOption}
                        installmentPlans={installmentPlans}
                        housing={housing}
                        shareData={shareData}
                        setShareData={setShareData}
                        hasMultiTypes={hasMultiTypes}
                        type={type}
                    />
                  )}
                </section>
              )}
            </>
        )}
      </div>
  );
};

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
  const t: PricingType = ownProps.type ?? "residential";

  if (t === "commerce") {
    return {
      installmentPlans: selectCommerceInstallmentPlanTypesList(state),
      pending: !!selectCommerceInstallmentPlanTypesPending(state),
      error: !!selectCommerceInstallmentPlanTypesError(state),

      pricesByPeriod: selectCommercePricesByPeriodData(state),
      pendingByPeriod: !!selectCommercePricesByPeriodPending(state),
      errorByPeriod: !!selectCommercePricesByPeriodError(state),

      pricesByFirstPayment: selectCommercePricesByFirstPaymentData(state),
      pendingByFirstPayment: !!selectCommercePricesByFirstPaymentPending(state),
      errorByFirstPayment: !!selectCommercePricesByFirstPaymentError(state),
    };
  }

  return {
    installmentPlans: selectInstallmentPlanTypesList(state),
    pending: !!selectInstallmentPlanTypesPending(state),
    error: !!selectInstallmentPlanTypesError(state),

    pricesByPeriod: selectPricesByPeriodData(state),
    pendingByPeriod: !!selectPricesByPeriodPending(state),
    errorByPeriod: !!selectPricesByPeriodError(state),

    pricesByFirstPayment: selectPricesByFirstPaymentData(state),
    pendingByFirstPayment: !!selectPricesByFirstPaymentPending(state),
    errorByFirstPayment: !!selectPricesByFirstPaymentError(state),
  };
};

const mapDispatchToProps = (dispatch, ownProps: OwnProps): DispatchProps => {
  const t: PricingType = ownProps.type ?? "residential";

  if (t === "commerce") {
    return {
      fetchInstallmentPlanTypes: (housingName: string) =>
          dispatch(HousingsActionCreator.fetchCommerceInstallmentPlanTypes(housingName)),
      fetchPricesByPeriod: (housingName: string) =>
          dispatch(HousingsActionCreator.fetchCommercePricesByPeriod(housingName)),
      fetchPricesByFirstPayment: (housingName: string) =>
          dispatch(HousingsActionCreator.fetchCommercePricesByFirstPayment(housingName)),
    };
  }

  return {
    fetchInstallmentPlanTypes: (housingName: string) =>
        dispatch(HousingsActionCreator.fetchInstallmentPlanTypes(housingName)),
    fetchPricesByPeriod: (housingName: string) =>
        dispatch(HousingsActionCreator.fetchPricesByPeriod(housingName)),
    fetchPricesByFirstPayment: (housingName: string) =>
        dispatch(HousingsActionCreator.fetchPricesByFirstPayment(housingName)),
  };
};

export const Pricing = connect(mapStateToProps, mapDispatchToProps)(_Pricing);
